import React, { useEffect } from "react";
import { useNavigate } from "react-router-dom";

// Utils
import { setItem } from "../../../utils/cache/LocalStorage";
import { POST } from "../../../services/HTTPServices";
import { Switch, FormControlLabel } from "@mui/material";

// Styled Components
import {
  FormContainer,
  Label,
  LoginButton,
  LoginTypeToggle,
} from "../../../styles/login";
import {
  LoadingOverlay,
  LoadingSpinnerIcon,
  LoadingSpinner,
} from "../../../styles/loading";
import {
  ErrorText,
  FormGroup,
  FullWidthContainer,
  Input,
  InputField,
  PhonePrefix,
} from "../../../styles/PersonalInfoStep";
import {
  PatternValidators,
  validPattern,
} from "../../../utils/validators";
import {
  ErrorMessage,
  Title,
  Subtitle,
} from "../../../styles/EnterEmail";

interface EnterEmailProps {
  callback?: () => void; // Optional callback function
}

const EnterEmail = (props: EnterEmailProps) => {
  const navigate = useNavigate();
  const [submitBtnClick, setSubmitBtnClick] = React.useState(false);
  const [loading, setLoading] = React.useState(true);
  const [loginType, setLoginType] = React.useState<string>("mobile");

  const [errors, setErrors] = React.useState<Record<string, boolean>>({
    email: true,
  });

  const [values, setValues] = React.useState({
    email: "",
  });
  const [errorMessage, setErrorMessage] = React.useState("");

  useEffect(() => {
    setLoading(false);
  }, []);

    const handleChange = (
      e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>
    ) => {
      const { name, value } = e.target;
  
      setValues({ ...values, [name]: value });
      validateField(name, value);
      setErrorMessage("")
    };

  const valid = () => {
    if (values.email === "") return false;
    else return true;
  };

  const confirm = async () => {
    await setSubmitBtnClick(true);
    const fieldsToValidate = ["email"];

    const validationResults = await Promise.all(
      fieldsToValidate.map((field) =>
        validateField(field, values[field as keyof typeof values])
      )
    );

    const hasErrors = validationResults.some((isValid) => !isValid);
    const isFormValid = !hasErrors;

    if (isFormValid) {
      setLoading(true);
      await setSubmitBtnClick(true);
      let url = "users/ForgotPasswordRequest";
      let tempUser = {
        email: (loginType === "mobile" ? "+65" : "") + values.email,
      };

      let res: any = await POST(url, tempUser, "user");
      if (res && res.status === 200) {
        setItem("forgot_email", values.email);
        navigate("/forgot-password/otp");
        setLoading(false);
      } else {
        if (res.status && res.status === 401) navigate("/unauthorized-page");
        else {
          setErrorMessage(res.message);
        }
        setLoading(false);
      }
    }
  };

  const validateField = async (prop: any, value: any) => {
    let isValid: boolean = true;

    try {
      switch (prop) {
        case "email": {
          isValid = await validPattern({
            paramPattern: value,
            patternType: loginType === "mobile" ? "mobile" : "email",
          });
          break;
        }
      }
    } catch (err) {}

    setErrors((prev) => ({
      ...prev,
      [prop]: !isValid, // Invert the error state to match your validation logic
    }));

    return isValid;
  };

  const toggleLoginType = () => {
    const newType = loginType === "mobile" ? "email" : "mobile";
    setLoginType(newType);
    setValues({ ...values, email: "" });
  };

  return (
    <>
      {loading && (
        <LoadingOverlay>
          <LoadingSpinner>
            <LoadingSpinnerIcon />
          </LoadingSpinner>
        </LoadingOverlay>
      )}
      <FormContainer>
        <Title className="mt-16">Forgot your password?</Title>

        <Subtitle>
          Enter your email and we'll send you a verification code to reset your
          password.
        </Subtitle>

        <LoginTypeToggle position="left">
          <FormControlLabel
            className="m-0"
            control={
              <Switch
                checked={loginType === "email"}
                onChange={toggleLoginType}
                color="primary"
                sx={{
                  "& .MuiSwitch-switchBase.Mui-checked": {
                    color: "#E39435",
                  },
                  "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track": {
                    backgroundColor: "#E39435",
                  },
                }}
                size="small"
              />
            }
            label="Login with Email"
            labelPlacement="start"
          />
        </LoginTypeToggle>
        <FormGroup $hasError={!!errors.email} className="mb-16">
          <FullWidthContainer>
            <Label>{loginType === "mobile" ? "Mobile" : "Email"}*</Label>
            <InputField $hasError={!!errors.email}>
              {loginType === "mobile" && <PhonePrefix>+65</PhonePrefix>}
              <Input
                type="text"
                name="email"
                placeholder={
                  loginType === "mobile"
                    ? "Enter your mobile"
                    : "Enter your email"
                }
                value={values.email}
                onChange={handleChange}
                $hasError={!!errors.email}
                className={loginType === "mobile" ? "ml-16" : "ml-negative-16"}
              />
            </InputField>
          </FullWidthContainer>
        </FormGroup>

        {submitBtnClick && errors.email && (
          <ErrorText>
            <PatternValidators
              paramPattern={values.email}
              patternType={loginType}
              textType={loginType === "mobile" ? "Mobile" : "Email"}
            />
          </ErrorText>
        )}

        {errorMessage && <ErrorMessage>{errorMessage}</ErrorMessage>}

        <LoginButton type="submit" onClick={confirm} disabled={!valid()}>
          Confirm
        </LoginButton>
      </FormContainer>
    </>
  );
};

export default EnterEmail;
